import type { ProductType } from "@/types";

type currencyOptions = {
  symbol: string;
  thousandsSeparator: string;
  fractionCount: number;
  fractionSeparator: string;
  symbolPosition: string;
  symbolSpacing: boolean;
  avoidEmptyDecimals: string;
};

export const formatterUtils = () => {
  const currency = (
    value: number = 0,
    options: currencyOptions | {} = {}
  ): string => {
    const config = {
      symbol: "$",
      thousandsSeparator: ",",
      fractionCount: 2,
      fractionSeparator: ".",
      symbolPosition: "front",
      symbolSpacing: false,
      avoidEmptyDecimals: "##",
    };

    // If options are provided, override the default config
    if (options) {
      Object.assign(config, options);
    }

    let result = value
      .toFixed(config.fractionCount)
      .replace(/\B(?=(\d{3})+(?!\d))/g, config.thousandsSeparator);

    // Remove ".00" if the number is a whole number
    // if (Number.isInteger(value)) {
    //   result = result.replace(/\.00$/, "");
    // }

    if (config.fractionSeparator !== ".") {
      result = result.replace(".", config.fractionSeparator);
    }

    if (config.symbolPosition === "front") {
      return `${config.symbol}${config.symbolSpacing ? " " : ""}${result}`;
    } else {
      return `${result}${config.symbolSpacing ? " " : ""}${config.symbol}`;
    }
  };

  const getProductPrice = (
    product: ProductType,
    withDiscount: boolean = true
  ) => {
    // Check if this is an affiliate product (products with AF_ category prefix)
    const isAffiliateProduct = product?.customFields?.affiliateProduct || 
      (product?.categories && product.categories.some(category => 
        category.code && category.code.toUpperCase().startsWith('AF_')
      ));
    
    // If it's an affiliate product, use a different pricing approach
    if (isAffiliateProduct) {
      // For affiliate products, use the oneTime price structure if available
      if (product?.productOfferingPrice?.oneTime && product.productOfferingPrice.oneTime.length > 0) {
        const oneTimePrice = product.productOfferingPrice.oneTime[0];
        if (withDiscount && oneTimePrice?.discount) {
          return Number(oneTimePrice?.price?.taxIncludedAmountAfterDiscount || 0);
        } else {
          return Number(oneTimePrice?.price?.taxIncludedAmount || 0);
        }
      }
      
      // Fallback to the first recurring price if oneTime is not available
      if (product?.productOfferingPrice?.recurring && product.productOfferingPrice.recurring.length > 0) {
        const firstPrice = product.productOfferingPrice.recurring[0];
        if (withDiscount && firstPrice?.discount) {
          return Number(firstPrice?.price?.taxIncludedAmountAfterDiscount || 0);
        } else {
          return Number(firstPrice?.price?.taxIncludedAmount || 0);
        }
      }
      
      // If all else fails, look for a price in custom fields
      return Number(product?.customFields?.price || 0);
    }
    
    // Standard pricing logic for non-affiliate products
    let finalPrice: number = 0;
    const productRecordId =
      product?.offerServiceTemplate[0]?.serviceTemplate.code;

    const priceObject = product?.productOfferingPrice?.recurring.find(
      (price) => price.id === Number(productRecordId)
    );
    if (withDiscount && priceObject?.discount) {
      finalPrice = Number(priceObject?.price?.taxIncludedAmountAfterDiscount || 0);
    } else {
      finalPrice = Number(priceObject?.price?.taxIncludedAmount || 0);
    }

    return finalPrice;
  };

  const getProductDiscountValue = (product: ProductType) => {
    // Check if this is an affiliate product
    const isAffiliateProduct = product?.customFields?.affiliateProduct || 
      (product?.categories && product.categories.some(category => 
        category.code && category.code.toUpperCase().startsWith('AF_')
      ));
    
    // For affiliate products
    if (isAffiliateProduct) {
      // Check oneTime price first
      if (product?.productOfferingPrice?.oneTime && product.productOfferingPrice.oneTime.length > 0) {
        const oneTimePrice = product.productOfferingPrice.oneTime[0];
        if (oneTimePrice?.discount) {
          return Number(oneTimePrice?.price?.discountAmount || 0);
        }
      }
      
      // Fallback to first recurring price
      if (product?.productOfferingPrice?.recurring && product.productOfferingPrice.recurring.length > 0) {
        const firstPrice = product.productOfferingPrice.recurring[0];
        if (firstPrice?.discount) {
          return Number(firstPrice?.price?.discountAmount || 0);
        }
      }
      
      // If there's a custom discount field, use that
      return Number(product?.customFields?.discountAmount || 0);
    }
    
    // Standard approach for non-affiliate products
    const productRecordId =
      product?.offerServiceTemplate[0]?.serviceTemplate.code;

    const priceObject = product?.productOfferingPrice?.recurring.find(
      (price) => price.id === Number(productRecordId)
    );
    if (!priceObject?.discount) return 0;

    return Number(priceObject?.price?.discountAmount || 0);
  };

  const getProductDiscountDetails = (product: ProductType) => {
    const productRecordId =
      product?.offerServiceTemplate[0]?.serviceTemplate.code;
    const priceObject = product?.productOfferingPrice?.recurring.find(
      (price) => price.id === Number(productRecordId)
    );
    if (!priceObject?.discount) return null;

    let discount = null;
    discount = product?.allowedDiscountPlans.find(
      (discount) => discount.code === priceObject?.discount?.discountPlanCode
    );
    return discount;
  };

  const getPlanDurationText = (product: ProductType) => {
    let text = "";

    const durationUnit =
      product?.offerServiceTemplate?.[0]?.serviceTemplate?.renewalRule
        ?.renewForUnit;
    const duration =
      product?.offerServiceTemplate?.[0]?.serviceTemplate?.renewalRule
        ?.renewFor;

    if (durationUnit === "MONTH") {
      text = `per month`;
    } else if (durationUnit === "DAY") {
      text = `${duration} days`;
    }
    return text;
  };

  const formatToUnit = (value: number, unit: string): string => {
    if (unit.toUpperCase() === "MB") {
      return `${(value / 1024).toFixed(0)}GB`;
    }
    return `${value}${unit}`;
  };

  return {
    currency,
    getProductPrice,
    getProductDiscountValue,
    getPlanDurationText,
    getProductDiscountDetails,
    formatToUnit,
  };
};
